export type SubTicketConflictAction = 'start_timer' | 'reassign' | 'complete' | 'archive';

export interface SubTicketConflict {
    message: string;
}

// A 409 means the request was well formed and the caller was allowed to make it, but the
// current state of the resource refuses it right now — in practice, a running timer. It is
// retryable, which is exactly what separates it from a 422.
//
// The server names the offending sub-ticket in `description`, so that text is always preferred;
// these fallbacks only cover a conflict that somehow arrives without one.
const FALLBACK_MESSAGES: { [action in SubTicketConflictAction]: string } = {
    start_timer: 'A timer is already running on another sub-ticket. Stop it before starting a new one.',
    reassign: 'A timer is running on this sub-ticket. Stop it before changing the designer.',
    complete: 'A timer is running on this sub-ticket. Stop it before marking the phase complete.',
    archive: 'A timer is running on this sub-ticket. Stop it before archiving the phase.',
};

// Returns null for anything that is not a conflict, so callers can rethrow and fall through to
// their ordinary error handling rather than mislabelling a 500 as a busy resource.
export const resolveSubTicketConflict = (error: any, action: SubTicketConflictAction): SubTicketConflict | null => {
    if (error?.response?.status !== 409) {
        return null;
    }

    const data = error?.response?.data;

    // The wire body carries `description`; `message` is only read as a defensive fallback
    const description = typeof data?.description === 'string' ? data.description.trim() : '';
    const message = typeof data?.message === 'string' ? data.message.trim() : '';

    return { message: description || message || FALLBACK_MESSAGES[action] };
};
